import {
  COUNTRY_OPTIONS as TRENDING_SOUNDS_COUNTRIES,
  DATE_RANGE_OPTIONS as TRENDING_SOUNDS_DATE_RANGES,
  DEFAULT_FILTERS as TRENDING_SOUNDS_DEFAULT_FILTERS,
  GENRE_OPTIONS as TRENDING_SOUNDS_GENRES,
} from '@/app/scheduler/components/EditPost/TikTokTrendingSounds/constants';
import { useTrendingHashtagsSearchArgs } from '@/app/tiktok/composables/useTrendingHashtagsSearchArgs';
import { getCountryFlag } from '@/utils/countries';

export const tooltips = {
  engagements:
    'The total number of engagements on your videos during the selected date range. Total Engagements = Likes + Comments + Shares',
  trendingVideos:
    'Your videos that received the highest number of views during the selected date range.',
  trendingVideosWithEntertainmentScore:
    'Your videos that received the highest number of views during the selected date range. These posts are compared to the average Entertainment Score from posts published in the last 12 weeks.',
  totalVideoViews: 'Total Video Views',
  avgCompletionRate:
    'The average percentage of this video that people watched. Retention Rate = Average Time Watched / Video Duration',
  fypViews: 'The total percentage of video views that came from the For You page.',
  effectivenessRate:
    'The percentage of people that saw and engaged with this video. Effectiveness Rate = (Likes + Comments + Shares + Video Views) / Reach',
  noBrandPermission: "Looks like this feature isn't included in your plan!",
  noUserPermission: "Looks like you don't have permission to use this feature!",
  contactCallToAction:
    'Dash Social brings you all of your essential social media solutions, including LikeShop! Ask your Dash Social administrator to provide you access.',
  connectCallToAction:
    "Dash Social brings you all of the insights you love, including TikTok! Ask your Dash Social administrator to provide you access. Or if this feature isn't in your package, contact your Customer Success Representative to try it out!",
  engagementRate:
    'The percentage of unique viewers that engaged with this video. Engagement Rate = Total Engagements / Reach',
  totalFollowers:
    'The total number of people who have followed your account by the last day of the selected date range.',
  netNewFollowers:
    'The net new number of people who have followed your account during the selected date range.',
  impressionsPersonalProfileRate:
    'The total percentage of video views that came from viewers on your personal profile.',
  fullVideoWatchedRate: 'The percentage of viewers that watched your entire video.',
  impressionsSearchRate:
    'The total percentage of video views that came from users performing a search.',
  impressionsSoundRate: "The total percentage of video views that came from the sound's page.",
  impressionsFollowRate:
    'The total percentage of video views that came from viewers on their Following page.',
  impressionsHashtagRate: 'The total percentage of video views that came from hashtags.',
  noConnection:
    'Before clicking Connect, log in to the correct TikTok Business account in a separate browser window. <br><br>' +
    "If this is a new connection, you'll need to provide your TikTok handle to your customer success rep first.",
  followerBreakdown: 'The top locations and gender breakdown of your account’s followers.',
  entertainmentScore: 'Entertainment Score',
  likeShopLinkCopyTooltip:
    'Click to copy. Paste this link into the website field of your TikTok profile on mobile.',
  ugcWordcloudTooltip:
    'The top caption keywords used in the 100 most recent videos that mention you in the past 90 days',
  ugcHashtagsTooltip:
    'Top videos from the last 90 days featuring your branded hashtags in the caption. Only hashtags submitted and approved by TikTok will be included.',
  ugcViewsHidden: 'The TikTok API does not provide a total for posts with under 1,000 video views',
  ugcReachHidden: 'The TikTok API does not provide reach for posts with under 1,000 video views',
};

export const timeScaleTypes = {
  DAILY: 'DAILY',
  MONTHLY: 'MONTHLY',
};

export const tiktokUGCMetricErrors = {
  TOO_OLD:
    "Metrics on this video are no longer up-to-date. TikTok's API does not support updated metrics on posts published more than 90 days ago.",
  NO_LONGER_RELEVANT:
    "Due to a limitation with TikTok's API, metrics on this video are no longer up-to-date.",
};

export const tiktokConnectionMessages = {
  title: 'Connect Your TikTok Account',
  message: 'You must have a Business TikTok account to access your insights in Dash Social.',
};

export const tiktokNoBrandUGCAccessMessage = {
  title: `Find Out Who’s Talking About Your Brand`,
  message: `Never miss an influential TikTok post that features your brand. Get insight into how your audience talks about you and engage with that brand love.`,
};

export const tiktokNoUserUGCAccessMessage = {
  title: `Looks Like You Don't Have Permission To Use This Feature!`,
  message: `Dash Social brings you all the insights you love, including User Generated Content. Ask your Dash Social administrator to provide you access.`,
};

export const tiktokTopPerformingVideosSectionInfo = {
  title: 'Top Performing Videos',
  tooltip: 'Your highest performing posts published within the selected date range.',
  emptyMessage: 'No TikTok posts found during this reporting period',
};

export const tiktokTrendingVideosSectionInfo = {
  title: 'Trending Videos',
  tooltip: 'Your videos with the highest video views during the selected reporting period.',
  emptyMessage: 'No TikTok posts found during this reporting period',
};

export const tiktokPostMetricKeys = {
  VIDEO_VIEWS: 'TIKTOK_VIEWS',
  TOTAL_ENGAGEMENTS: 'TIKTOK_TOTAL_ENGAGEMENTS',
  EFFECTIVENESS: 'TIKTOK_EFFECTIVENESS',
  ENGAGEMENT_RATE: 'TIKTOK_ENGAGEMENT_RATE',
  RETENTION_RATE: 'TIKTOK_AVERAGE_COMPLETION_RATE',
  FYP_VIEWS: 'TIKTOK_IMPRESSIONS_FOR_YOU_RATE',
  DATE: 'DATE',
  IMPRESSIONS_PERSONAL_PROFILE_RATE: 'TIKTOK_IMPRESSIONS_PERSONAL_PROFILE_RATE',
  AVERAGE_TIME_WATCHED: 'TIKTOK_AVERAGE_TIME_WATCHED',
  REACH: 'TIKTOK_REACH',
  COMMENTS: 'TIKTOK_COMMENTS',
  FULL_VIDEO_WATCHED_RATE: 'TIKTOK_FULL_VIDEO_WATCHED_RATE',
  IMPRESSIONS_SEARCH_RATE: 'TIKTOK_IMPRESSIONS_SEARCH_RATE',
  SHARES: 'TIKTOK_SHARES',
  IMPRESSIONS_SOUND_RATE: 'TIKTOK_IMPRESSIONS_SOUND_RATE',
  IMPRESSIONS_FOLLOW_RATE: 'TIKTOK_IMPRESSIONS_FOLLOW_RATE',
  TOTAL_TIME_WATCHED: 'TIKTOK_TOTAL_TIME_WATCHED',
  IMPRESSIONS_HASHTAG_RATE: 'TIKTOK_IMPRESSIONS_HASHTAG_RATE',
  DURATION: 'TIKTOK_DURATION',
  LIKES: 'TIKTOK_LIKES',
  ENTERTAINMENT_SCORE: 'TIKTOK_ENTERTAINMENT_SCORE',
};

const tiktokPostDefaultMetrics = [
  tiktokPostMetricKeys.ENTERTAINMENT_SCORE,
  tiktokPostMetricKeys.VIDEO_VIEWS,
  tiktokPostMetricKeys.TOTAL_ENGAGEMENTS,
  tiktokPostMetricKeys.AVERAGE_TIME_WATCHED,
];

const tiktokPostMetricsV2 = [tiktokPostMetricKeys.ENTERTAINMENT_SCORE];

export const tiktokMediaCardDefaults = {
  metrics: ['entertainmentScore', 'views', 'totalEngagements', 'averageTimeWatched'],
  labels: {
    entertainmentScore: 'Entertainment Score',
    views: 'Video Views',
    totalEngagements: 'Total Engagements',
    averageTimeWatched: 'Avg. Time Watched',
  },
};

export const tiktokGalleryCardDefaults = {
  metrics: [
    'tiktokTotalVideoViews',
    'tiktokAvgVideoViews',
    'tiktokAvgRetentionRate',
    'tiktokAvgEngagementRate',
  ],
  labels: {
    tiktokTotalVideoViews: 'Total Video Views',
    tiktokAvgVideoViews: 'Avg. Video Views',
    tiktokAvgRetentionRate: 'Avg. Retention Rate',
    tiktokAvgEngagementRate: 'Avg. Engagement Rate',
  },
};

export const tiktokMetricFormats = {
  tiktokAvgRetentionRate: 'percent',
  tiktokAvgEngagementRate: 'percent',
};

const tiktokPostMetrics = {};
tiktokPostMetrics[tiktokPostMetricKeys.VIDEO_VIEWS] = {
  field: 'views',
  label: 'Video Views',
};
tiktokPostMetrics[tiktokPostMetricKeys.TOTAL_ENGAGEMENTS] = {
  field: 'totalEngagements',
  label: 'Total Engagements',
};
tiktokPostMetrics[tiktokPostMetricKeys.EFFECTIVENESS] = {
  field: 'effectiveness',
  label: 'Effectiveness',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.ENGAGEMENT_RATE] = {
  field: 'engagementRate',
  label: 'Engagement Rate',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.ENTERTAINMENT_SCORE] = {
  field: 'entertainmentScore',
  label: 'Entertainment Score',
  format: 'two_decimal_float',
};
tiktokPostMetrics[tiktokPostMetricKeys.RETENTION_RATE] = {
  field: 'averageCompletionRate',
  label: 'Retention Rate',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.FYP_VIEWS] = {
  field: 'impressionsForYouRate',
  label: 'FYP Views %',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.DATE] = {
  label: 'Recently Published',
};
tiktokPostMetrics[tiktokPostMetricKeys.IMPRESSIONS_PERSONAL_PROFILE_RATE] = {
  field: 'impressionsPersonalProfileRate',
  label: 'Profile Views %',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.AVERAGE_TIME_WATCHED] = {
  field: 'averageTimeWatched',
  label: 'Avg. Time Watched',
  format: 'duration',
  num_decimals: 1,
};
tiktokPostMetrics[tiktokPostMetricKeys.REACH] = {
  field: 'reach',
  label: 'Reach',
};
tiktokPostMetrics[tiktokPostMetricKeys.COMMENTS] = {
  field: 'comments',
  label: 'Comments',
};
tiktokPostMetrics[tiktokPostMetricKeys.FULL_VIDEO_WATCHED_RATE] = {
  field: 'fullVideoWatchedRate',
  label: 'Completion Rate',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.IMPRESSIONS_SEARCH_RATE] = {
  field: 'impressionsSearchRate',
  label: 'Search Views %',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.SHARES] = {
  field: 'shares',
  label: 'Shares',
};
tiktokPostMetrics[tiktokPostMetricKeys.IMPRESSIONS_SOUND_RATE] = {
  field: 'impressionsSoundRate',
  label: 'Sound Views %',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.IMPRESSIONS_FOLLOW_RATE] = {
  field: 'impressionsFollowRate',
  label: 'Following Page Views %',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.TOTAL_TIME_WATCHED] = {
  field: 'totalTimeWatched',
  label: 'Total Time Watched',
  format: 'duration',
};
tiktokPostMetrics[tiktokPostMetricKeys.IMPRESSIONS_HASHTAG_RATE] = {
  field: 'impressionsHashtagRate',
  label: 'Hashtag Views %',
  format: 'percent',
};
tiktokPostMetrics[tiktokPostMetricKeys.DURATION] = {
  field: 'duration',
  label: 'Video Duration',
  format: 'duration',
};
tiktokPostMetrics[tiktokPostMetricKeys.LIKES] = {
  field: 'likes',
  label: 'Likes',
};

const yourPostsSortOptions = [
  {
    value: 'DATE',
    label: 'Recently Published',
  },
  {
    value: 'TIKTOK_IMPRESSIONS_PERSONAL_PROFILE_RATE',
    label: 'Profile Views %',
    field: 'impressionsPersonalProfileRate',
  },
  {
    value: 'TIKTOK_AVERAGE_TIME_WATCHED',
    label: 'Avg. Time Watched',
    field: 'averageTimeWatched',
  },
  {
    value: 'TIKTOK_REACH',
    label: 'Reach',
    field: 'reach',
  },
  {
    value: 'TIKTOK_COMMENTS',
    label: 'Comments',
    field: 'comments',
  },
  {
    value: 'TIKTOK_AVERAGE_COMPLETION_RATE',
    label: 'Retention Rate',
    field: 'averageCompletionRate',
  },
  {
    value: 'TIKTOK_FULL_VIDEO_WATCHED_RATE',
    label: 'Completion Rate',
    field: 'fullVideoWatchedRate',
  },
  {
    value: 'TIKTOK_IMPRESSIONS_SEARCH_RATE',
    label: 'Search Views %',
    field: 'impressionsSearchRate',
  },
  {
    value: 'TIKTOK_ENGAGEMENT_RATE',
    label: 'Engagement Rate',
    field: 'engagementRate',
  },
  {
    value: 'TIKTOK_SHARES',
    label: 'Shares',
    field: 'shares',
  },
  {
    value: 'TIKTOK_ENTERTAINMENT_SCORE',
    label: 'Entertainment Score',
    field: 'entertainmentScore',
  },
  {
    value: 'TIKTOK_IMPRESSIONS_SOUND_RATE',
    label: 'Sound Views %',
    field: 'impressionsSoundRate',
  },
  {
    value: 'TIKTOK_IMPRESSIONS_FOLLOW_RATE',
    label: 'Following Page Views %',
    field: 'impressionsFollowRate',
  },
  {
    value: 'TIKTOK_TOTAL_ENGAGEMENTS',
    label: 'Total Engagements',
    field: 'totalEngagements',
  },
  {
    value: 'TIKTOK_IMPRESSIONS_FOR_YOU_RATE',
    label: 'For You Page Views %',
    field: 'impressionsForYouRate',
  },
  {
    value: 'TIKTOK_TOTAL_TIME_WATCHED',
    label: 'Total Time Watched',
    field: 'totalTimeWatched',
  },
  {
    value: 'TIKTOK_IMPRESSIONS_HASHTAG_RATE',
    label: 'Hashtag Views %',
    field: 'impressionsHashtagRate',
  },
  {
    value: 'TIKTOK_DURATION',
    label: 'Video Duration',
    field: 'duration',
  },
  {
    value: 'TIKTOK_LIKES',
    label: 'Likes',
    field: 'likes',
  },
  {
    value: 'TIKTOK_VIEWS',
    label: 'Video Views',
    field: 'views',
  },
];

const galleriesSortOptions = [
  {
    value: 'CREATED',
    label: 'Recently Added',
  },
  {
    value: 'TIKTOK_AVG_ENGAGEMENT_RATE',
    label: 'Avg. Engagement Rate',
  },
  {
    value: 'TIKTOK_AVG_RETENTION_RATE',
    label: 'Avg. Retention Rate',
  },
  {
    value: 'TIKTOK_AVG_VIDEO_VIEWS',
    label: 'Avg. Video Views',
  },
  {
    value: 'TIKTOK_TOTAL_VIDEO_VIEWS',
    label: 'Total Video Views',
  },
];

const ugcDefaultSort = 'DATE';
const UGC_SORT_OPTIONS = Object.freeze([
  {
    value: 'DATE',
    label: 'Recently Published',
  },
  {
    value: 'TIKTOK_VIEWS',
    label: 'Video Views',
    field: 'views',
  },
  {
    value: 'TIKTOK_REACH',
    label: 'Reach',
    field: 'reach',
  },
  {
    value: 'TIKTOK_TOTAL_ENGAGEMENTS',
    label: 'Engagements',
    field: 'totalEngagements',
  },
  {
    value: 'TIKTOK_COMMENTS',
    label: 'Comments',
    field: 'comments',
  },
  {
    value: 'TIKTOK_LIKES',
    label: 'Likes',
    field: 'likes',
  },
  {
    value: 'TIKTOK_SHARES',
    label: 'Shares',
    field: 'shares',
  },
]);

const UGC_HASHTAG_SORT_OPTIONS = Object.freeze([
  {
    value: 'DATE',
    label: 'Recently Published',
  },
  {
    value: 'TIKTOK_TOTAL_ENGAGEMENTS',
    label: 'Engagements',
    field: 'totalEngagements',
  },
  {
    value: 'TIKTOK_LIKES',
    label: 'Likes',
    field: 'likes',
  },
  {
    value: 'TIKTOK_COMMENTS',
    label: 'Comments',
    field: 'comments',
  },
  {
    value: 'TIKTOK_SHARES',
    label: 'Shares',
    field: 'shares',
  },
]);

export const UGC_DEFAULT_SORT = Object.freeze({
  sortOrder: 'DESC',
  selected: UGC_SORT_OPTIONS[0],
});

export const topPerformingVideosSortOptions = [
  {
    value: '-TIKTOK_ENTERTAINMENT_SCORE',
    label: 'Entertainment Score',
  },
  {
    value: '-TIKTOK_VIEWS',
    label: 'Video Views',
  },
];

const galleriesFilterWidgetsConfig = {
  tags: {
    name: 'MediaFilterTagsWidget',
    label: 'Tags',
    noDuplicate: true,
    options: {
      tagsList: [],
    },
  },
};

const galleriesFilterOptions = [
  {
    label: 'Tags',
    value: 'tags',
  },
];

export const genderDistributionChartLabels = {
  MALE: 'male',
  FEMALE: 'female',
  UNKNOWN: 'unknown',
};

export const tiktokCommentRequiredScopes = ['comment.list', 'comment.list.manage'];

export const tiktokDirectMessageRequiredScopes = [
  'message.list.manage',
  'message.list.read',
  'message.list.send',
];

export const TIKTOK_ACCOUNT_REGION = {
  US: 'US',
  EU: 'EU',
  SEA: 'SEA',
  UNKNOWN: 'UNKNOWN',
};

export const tikTokLikeShopCustomContentSettings = {
  pinnedItemLimit: 3,
  bannerLinksLimit: 1,
  bannerTitle: 'Banner Image',
  bannerMessage: '1080px width or greater required. 2:1 aspect ratio recommended.',
  pinnedContentTitle: 'Pin Content',
  pinnedContentMessage:
    'Pin up to three pieces. At least one link is required on each piece of content.',
  existingMediaErrorMessage:
    'Existing LikeShop media cannot be used as a banner image or pinned content.',
  bannerWidthErrorMessage:
    'Your banner has a low resolution which will compromise its quality when published. Please add an image with a width of 1080px or greater.',
  pinnedContentWidthWarningMessage:
    'One or more of your images are low resolution, which may compromise its quality when published. We recommend using an image with a width of 1080px or greater.',
  pinnedImageLinksLimit: 25,
  recommendedCustomContentWidth: 1080,
  pinnedContentMissingLinksMessage: 'Pinned content must contain links.',
  hasUploadingMediaMessage: 'Please wait for media to finish uploading.',
};

export const mostEntertainingPostsSortOptions = [
  {
    value: '-TIKTOK_ENTERTAINMENT_SCORE',
    label: 'Entertainment Score',
  },
  {
    value: '-TIKTOK_VIEWS',
    label: 'Video Views',
  },
];

export const tiktokUgcMediaCardDefaults = {
  labels: {
    views: 'Video Views',
    reach: 'Reach',
    totalEngagements: 'Engagements',
    likes: 'Likes',
    comments: 'Comments',
    shares: 'Shares',
  },
  metrics: ['views', 'reach', 'totalEngagements', 'shares'],
};

export const tiktokUgcHashtagMediaCardMetrics = {
  labels: {
    totalEngagements: 'Engagements',
    likes: 'Likes',
    comments: 'Comments',
    shares: 'Shares',
  },
  metrics: ['totalEngagements', 'likes', 'comments', 'shares'],
};

export const tiktokUgcDateOptions = [
  { label: 'Last 24 Hours', value: 1 },
  { label: 'Last 3 Days', value: 3 },
  { label: 'Last 7 Days', value: 7 },
  { label: 'Last 4 Weeks', value: 28 },
];

export const tiktokUgcNoKeywordsFound =
  'Keywords will be displayed here when your brand is mentioned in video captions within the last 90 days.';
export const tiktokUgcKeywordsError =
  'We are currently unable to retrieve Top Keywords from TikTok. Please try again later.';

export const tiktokUgcKeywordWeights = {
  SINGLE_KEYWORD: '400',
  MULTI_KEYWORD: '500',
  HOVER_ANY_KEYWORD: '700',
};

export const tiktokUgcTruncatedKeywordLength = {
  KEYWORDS: 30,
};

export const tiktokTopLineStatsMetrics = [
  { value: 'VIDEO_VIEWS', label: 'Video Views' },
  { value: 'TOTAL_FOLLOWERS', label: 'Total Followers', tooltip: tooltips.totalFollowers },
  { value: 'NET_NEW_FOLLOWERS', label: 'Net New Followers', tooltip: tooltips.netNewFollowers },
  { value: 'LIKES', label: 'Likes', overrideContextMetricName: 'ORGANIC_LIKES' },
  { value: 'COMMENTS', label: 'Comments', overrideContextMetricName: 'ORGANIC_COMMENTS' },
  { value: 'SHARES', label: 'Shares' },
  { value: 'PROFILE_VIEWS', label: 'Profile Views' },
];

export const analyticsDisabledForPersonalAccountLinkToReadMore =
  'https://help.dashsocial.com/hc/en-us/articles/6045974975757-Connecting-Your-TikTok-Account-to-Dash-Hudson';
export const analyticsDisabledForPersonalAccountStatus = 'ANALYTICS_DISABLED_FOR_PERSONAL_ACCOUNT';

export const trendingHashtagsTooltips = {
  title: 'Stay up to date on the most popular hashtags currently used in TikTok captions',
  ageChartTooltip: 'The approximate percentage of viewers associated with different age groups',
  interestGraphTooltip:
    'Each data point compares the relative volume against the peak volume of the trend. A value of 100 is the peak popularity for the trend.',
};

export const trendingRankChangeNew = 'NEW';

const COUNTRY_CODE_OPTIONS = useTrendingHashtagsSearchArgs().countryCodes.map((country) => ({
  label: `${getCountryFlag(country.value)} ${country.label}`,
  value: country.value,
}));

const TRENDING_SOUNDS_COUNTRY_OPTIONS = TRENDING_SOUNDS_COUNTRIES.map((country) => ({
  label: `${getCountryFlag(country.value)} ${country.label}`,
  value: country.value,
}));

export const TIKTOK_FILTER_DETAILS = Object.freeze({
  TRENDING_SOUNDS: {
    scope: 'tiktokTrendingSounds',
    options: {
      dateRange: TRENDING_SOUNDS_DATE_RANGES,
      country: TRENDING_SOUNDS_COUNTRY_OPTIONS,
      genre: TRENDING_SOUNDS_GENRES,
    },
    default: {
      dateRange: { preset: TRENDING_SOUNDS_DEFAULT_FILTERS.dateRange },
      country: TRENDING_SOUNDS_DEFAULT_FILTERS.country,
      genre: TRENDING_SOUNDS_DEFAULT_FILTERS.genre,
    },
  },
  TRENDING_HASHTAGS: {
    scope: 'tiktokTrendingHashtags',
    options: {
      reportingPeriod: useTrendingHashtagsSearchArgs().dateRanges,
      location: COUNTRY_CODE_OPTIONS,
      industry: useTrendingHashtagsSearchArgs().categoryNames.filter(
        (industry) => industry.value !== 'ALL',
      ),
    },
    default: {
      reportingPeriod: { preset: '7DAY' },
      location: 'US',
      industry: undefined,
    },
  },
  UGC: {
    scope: 'tiktokUGC',
    options: {
      isHashtag: [
        { value: false, label: 'Posts Featuring You' },
        { value: true, label: 'Posts Using Your Hashtags', tooltip: tooltips.ugcHashtagsTooltip },
      ],
    },
    default: {
      reportingPeriod: { preset: 'LAST_7_DAYS' },
      isHashtag: false,
    },
  },
});

export const UGC_LIMIT_EMPTY_STATE = {
  title: 'Experience Unlimited UGC',
  message: (limit) =>
    `Your current subscription is limited to displaying ${limit || '50'} post${limit === 1 ? '' : 's'}. Trial the Advance Social Plan to unlock more UGC.`,
};

export const PROMOTION_END_BANNER_MESSAGES = {
  withDate: (formattedDate) =>
    `Last promotion metrics received on ${formattedDate}, organic and promoted breakdowns are now final. Total video metrics will continue updating.`,
  generic:
    'Promotions have ended, organic and promoted metric breakdowns are now final. Total video metrics will continue updating.',
};

/**
 * The date when we started monitoring and tracking promoted TikTok ad metrics.
 * Videos promoted before this date cannot be reliably tracked, so promotion end dates
 * before this cutoff are not displayed to users.
 */
export const PROMOTION_METRICS_TRACKING_START_DATE = '2025-11-01';

export const tiktokPromotedMediaPopupMetrics = [
  {
    key: 'avgTimeWatched',
    label: 'Avg. Time Watched',
    time: true,
  },

  {
    key: 'comments',
    label: 'Comments - Total',
  },

  {
    key: 'completionRate',
    label: 'Completion Rate - Total',
    percent: true,
  },

  {
    key: 'engagementRate',
    label: 'Engagement Rate (R) - Total',
    percent: true,
  },
  {
    key: 'paidEngagementRate',
    label: 'Engagement Rate (R) - Promoted',
    parentKey: 'engagementRate',
    scopedMetric: true,
    nested: false,
    percent: true,
  },

  {
    key: 'viewsBasedEngagementRate',
    label: 'Engagement Rate (V) - Total',
    percent: true,
  },
  {
    key: 'paidViewsBasedEngagementRate',
    parentKey: 'viewsBasedEngagementRate',
    label: 'Engagement Rate (V) - Promoted',
    nested: false,
    percent: true,
  },

  {
    key: 'totalEngagements',
    label: 'Engagements - Total',
  },
  {
    key: 'paidEngagements',
    parentKey: 'totalEngagements',
    label: 'Engagements - Promoted',
    nested: false,
  },

  {
    key: 'favorites',
    label: 'Favorites - Total',
  },

  {
    key: 'newFollowers',
    label: 'Follows - Total',
  },
  {
    key: 'organicNewFollowers',
    parentKey: 'newFollowers',
    label: 'Follows - Organic',
    nested: false,
  },
  {
    key: 'paidNewFollowers',
    parentKey: 'newFollowers',
    label: 'Follows - Promoted',
    nested: false,
  },

  {
    key: 'likes',
    label: 'Likes - Total',
  },
  {
    key: 'organicLikes',
    parentKey: 'likes',
    label: 'Likes - Organic',
    nested: false,
  },
  {
    key: 'paidLikes',
    parentKey: 'likes',
    label: 'Likes - Promoted',
    nested: false,
  },

  {
    key: 'likeshopClicks',
    label: 'LikeShop Clicks',
  },

  {
    key: 'totalProfileViews',
    label: 'Profile Views - Total',
  },
  {
    key: 'organicProfileViews',
    parentKey: 'totalProfileViews',
    label: 'Profile Views - Organic',
    nested: false,
  },
  {
    key: 'paidProfileViews',
    parentKey: 'totalProfileViews',
    label: 'Profile Views - Promoted',
    nested: false,
  },

  {
    key: 'reach',
    label: 'Reach - Total',
  },
  {
    key: 'organicReach',
    parentKey: 'reach',
    label: 'Reach - Est. Organic',
    nested: false,
  },
  {
    key: 'paidReach',
    label: 'Reach - Promoted',
    parentKey: 'reach',
    scopedMetric: true,
    nested: false,
  },

  {
    key: 'retentionRate',
    label: 'Retention Rate - Total',
    percent: true,
  },

  {
    key: 'shares',
    label: 'Shares - Total',
  },
  {
    key: 'organicShares',
    parentKey: 'shares',
    label: 'Shares - Organic',
    nested: false,
  },
  {
    key: 'paidShares',
    parentKey: 'shares',
    label: 'Shares - Promoted',
    nested: false,
  },

  {
    key: 'totalTimeWatched',
    label: 'Time Watched - Total',
    time: true,
  },

  {
    key: 'videoDuration',
    label: 'Video Duration - Total',
    time: true,
  },

  {
    key: 'videoViews',
    label: 'Video Views - Total',
  },
  {
    key: 'organicViews',
    parentKey: 'videoViews',
    label: 'Video Views - Est. Organic',
    nested: false,
  },
  {
    key: 'paidViews',
    parentKey: 'videoViews',
    label: 'Video Views - Promoted',
    nested: false,
  },
];
export const TRENDING_SOUND_DISCLAIMER_PARAGRAPHS = [
  'All content accessed through the Dash Social TikTok Trending Sound feature on the Dash Social Platform ("Trending Sound Feature") is retained by the applicable third-party rights holder and is subject to the rights of such third-party and the applicable TikTok terms governing the use of the content, including but not limited to the TikTok Terms of Service, Community Guidelines, Privacy Policy, Privacy Policy for Young Users and Intellectual Property Policy, including where applicable the TikTok Commercial Music Library User Terms governing commercially available audio (the "TikTok Terms").',
  "Dash Social is not responsible for your use of any content made available on the Trending Sound Feature. You are solely responsible for your use of the content made available on Trending Sounds Feature and for verifying your right to use the content on the Trending Sound Feature, including without limitation, verifying any copyrights, trademarks, or other rights as applicable and compliance with the TikTok Terms, including confirming that any audio made available through TikTok's Commercial Music Library remains available and authorized for your intended use at the time of use and thereafter.",
  "Availability of audio through TikTok's Commercial Music Library does not guarantee continued availability or ongoing rights to use such content, and such audio may be removed or restricted by TikTok or applicable rights holders at any time.\nThe use of the copyrighted content of others without proper authorization may constitute copyright infringement of rights of a third-party, be a violation of the terms of the Dash Social Platform and violate the TikTok Terms.",
];

export default {
  galleriesFilterOptions,
  galleriesFilterWidgetsConfig,
  galleriesSortOptions,
  tiktokPostDefaultMetrics,
  tiktokPostMetrics,
  tiktokPostMetricsV2,
  topPerformingVideosSortOptions,
  yourPostsSortOptions,
  ugcDefaultSort,
  UGC_SORT_OPTIONS,
  UGC_HASHTAG_SORT_OPTIONS,
};
